import { Flex, Select, Slider } from '@byecode/ui'
import type { BackgroundType, BlockType } from '@lighthouse/core'
import { BACKGROUND_TYPE, DESIGN_THEME, type DesignThemeType } from '@lighthouse/core'
import type { ThemeDesignInitConfig } from '@lighthouse/shared'
import { ListItem4ByecodeUi, themeBlockDesignConfig, themeFieldDesignConfig, useAppThemeContext } from '@lighthouse/shared'
import isDeepEqual from 'fast-deep-equal'
import React, { useCallback, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { InheritLabel } from '@/components/InheritLabel'

import { BackgroundField } from '../components/BackgroundField'
import { BorderField } from '../components/BorderField'
import { FillColor } from '../components/FillColor'
import { RadiusSize } from '../components/RadiusSize'
import { ShadowField } from '../components/ShadowField'
import { TextBorder } from '../components/TextBorder'
import { VeinsField } from '../components/VeinsField'
import { THEME_OPTIONS } from '../constants'
import { useSetExtendsKeys } from '../hooks/useMouseEventDistance'

export interface DesignSettingSwitch {
    enableTheme?: boolean
    enableRadius?: boolean
    enableLabelColor?: boolean
    enableColor?: boolean
    enableIconColor?: boolean
    enablePlaceholderColor?: boolean
    enableBackground?: boolean
    enableVeins?: boolean
    enableBorder?: boolean
    enableShadow?: boolean
    enableBlur?: boolean
    enableLineColor?: boolean
    enableCheckedColor?: boolean
    enableTextBorder?: boolean
}

export type DesignColorSetting = {
    background?: BackgroundType[]
    color?: BackgroundType[]
    textBorder?: BackgroundType[]
}
interface DesignSettingProps extends DesignSettingSwitch {
    beforeSlot?: React.ReactNode
    afterSlot?: React.ReactNode
    prefixName?: string
    customViewDsId?: string
    blockType?: BlockType
    lineLabel?: string
    colorLabel?: string
    titleLabel?: string
    backgroundLabel?: string
    theme?: DesignThemeType
    colorSetting?: DesignColorSetting
}

export const DesignSetting: React.FC<DesignSettingProps> = ({
    customViewDsId,
    blockType,
    prefixName = 'breakPoint',

    enableTheme,
    enableRadius,
    enableLabelColor,
    enablePlaceholderColor,
    enableColor,
    enableIconColor,
    enableBackground,
    enableVeins,
    enableBorder,
    enableShadow,
    enableBlur,
    enableLineColor,
    enableCheckedColor,
    enableTextBorder,
    lineLabel = '线条',
    colorLabel = '填充',
    titleLabel = '标题颜色',
    backgroundLabel = '背景',
    theme,
    colorSetting,
    beforeSlot,
    afterSlot
}) => {
    const { control, setValue, getValues } = useFormContext()
    const { handleSetBreakKey, handleSetBreakKeys, handleRemoveBreakKeys } = useSetExtendsKeys()
    const { theme: appTheme } = useAppThemeContext()
    const isVisibleSetting = useMemo(() => !enableTheme || !theme || theme === DESIGN_THEME.CUSTOM, [enableTheme, theme])
    // const isVisibleSetting = true
    const handleResetThemeKey = useCallback(() => {
        handleRemoveBreakKeys(['design', 'hoverStyle', 'activeStyle', 'disabledStyle'])
    }, [handleRemoveBreakKeys])

    const handleGetDesignAndInteractionStyleKeys = useCallback((prefix: string, config: ThemeDesignInitConfig | undefined): string[] => {
        const designKeys = config?.design
            ? Object.keys(config.design).map(key => {
                  return `${prefix}.design.${key}`
              })
            : []
        const hoverStyleKeys = config?.hoverStyle
            ? Object.keys(config.hoverStyle).map(key => {
                  return `${prefix}.hoverStyle.${key}`
              })
            : []
        const activeStyleKeys = config?.activeStyle
            ? Object.keys(config.activeStyle).map(key => {
                  return `${prefix}.activeStyle.${key}`
              })
            : []
        const disabledStyleKeys = config?.disabledStyle
            ? Object.keys(config.disabledStyle).map(key => {
                  return `${prefix}.disabledStyle.${key}`
              })
            : []

        return [...designKeys, ...hoverStyleKeys, ...activeStyleKeys, ...disabledStyleKeys]
    }, [])

    const handleThemeInitDesignConfig = useCallback(
        (config: ThemeDesignInitConfig) => {
            const keys = handleGetDesignAndInteractionStyleKeys(prefixName, config)
            const personDesignKeys = handleGetDesignAndInteractionStyleKeys(`${prefixName}.personDesign`, config.personDesign)
            const fileDesignKeys = handleGetDesignAndInteractionStyleKeys(`${prefixName}.fileDesign`, config.fileDesign)
            const sliderDesignKeys = handleGetDesignAndInteractionStyleKeys(`${prefixName}.sliderDesign`, config.sliderDesign)
            const selectDesignKeys = handleGetDesignAndInteractionStyleKeys(`${prefixName}.selectDesign`, config.selectDesign)
            setValue(prefixName, {
                ...getValues(prefixName),
                ...config
            })
            handleSetBreakKeys([
                `${prefixName}.design.theme`,
                ...keys,
                ...personDesignKeys,
                ...fileDesignKeys,
                ...sliderDesignKeys,
                ...selectDesignKeys
            ])
        },
        [getValues, handleGetDesignAndInteractionStyleKeys, handleSetBreakKeys, prefixName, setValue]
    )

    const handleThemeChange = useCallback(
        (val: string, onChange: (val: string) => void) => {
            if (blockType === 'field') {
                const inputType = getValues('inputType')
                const mode = getValues('relativeSelect.showMode')
                // 如果有输入框类型
                if (inputType) {
                    const relativeSelectMode = inputType === 'relativeSelect' ? mode : undefined
                    const config = themeFieldDesignConfig(inputType, { theme: appTheme, relativeSelectMode })?.[val as DesignThemeType]
                    if (config) {
                        onChange(val)
                        handleThemeInitDesignConfig(config)
                        return
                    }
                    // 当转化为自定义时且之前有主题
                    if (theme && theme !== 'CUSTOM') {
                        const config = themeFieldDesignConfig(inputType, { theme: appTheme, relativeSelectMode })?.[theme]
                        if (config) {
                            config.design = {
                                ...config.design,
                                theme: val as DesignThemeType
                            }
                            onChange(val)
                            handleThemeInitDesignConfig(config)
                            return
                        }
                    }
                }
            }
            if (blockType === 'filter') {
                const config = themeBlockDesignConfig(blockType, appTheme)?.[val as DesignThemeType]
                if (config) {
                    onChange(val)
                    handleThemeInitDesignConfig(config)
                    return
                }
                if (theme && theme !== 'CUSTOM') {
                    const config = themeBlockDesignConfig(blockType, appTheme)?.[theme]
                    if (config) {
                        config.design = {
                            ...config.design,
                            theme: val as DesignThemeType
                        }
                        onChange(val)
                        handleThemeInitDesignConfig(config)
                        return
                    }
                }
            }
            onChange(val)
            handleSetBreakKey(`${prefixName}.design.theme`)
        },
        [blockType, handleSetBreakKey, prefixName, getValues, appTheme, theme, handleThemeInitDesignConfig]
    )

    return (
        <>
            {isVisibleSetting && beforeSlot}
            {enableTheme && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="样式" name={`${prefixName}.design.theme`} onReset={handleResetThemeKey} />
                    <Controller
                        control={control}
                        name={`${prefixName}.design.theme`}
                        render={({ field }) => {
                            return (
                                <Select
                                    value={field.value}
                                    style={{ width: 180 }}
                                    options={THEME_OPTIONS}
                                    onChange={val => handleThemeChange(val, field.onChange)}
                                />
                            )
                        }}
                    />
                </ListItem4ByecodeUi>
            )}
            {isVisibleSetting && enableLabelColor && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label={titleLabel} name={`${prefixName}.design.labelColor`} />
                    <Controller
                        control={control}
                        name={`${prefixName}.design.labelColor`}
                        render={({ field }) => {
                            return (
                                <FillColor
                                    label={titleLabel}
                                    value={field.value}
                                    onChange={val => {
                                        field.onChange(val)
                                        handleSetBreakKey(`${prefixName}.design.labelColor`)
                                    }}
                                />
                            )
                        }}
                    />
                </ListItem4ByecodeUi>
            )}
            {isVisibleSetting && enableColor && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label={colorLabel} name={`${prefixName}.design.color`} />
                    <Controller
                        control={control}
                        name={`${prefixName}.design.color`}
                        render={({ field }) => {
                            return (
                                <FillColor
                                    label={colorLabel}
                                    value={field.value}
                                    enabledBackgroundTypes={colorSetting?.color}
                                    onChange={val => {
                                        field.onChange(val)
                                        handleSetBreakKey(`${prefixName}.design.color`)
                                    }}
                                />
                            )
                        }}
                    />
                </ListItem4ByecodeUi>
            )}
            {isVisibleSetting && enableIconColor && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="图标颜色" name={`${prefixName}.design.iconColor`} />
                    <Controller
                        control={control}
                        name={`${prefixName}.design.iconColor`}
                        render={({ field }) => {
                            return (
                                <FillColor
                                    label="图标颜色"
                                    value={field.value}
                                    enabledBackgroundTypes={[BACKGROUND_TYPE.color]}
                                    onChange={val => {
                                        field.onChange(val)
                                        handleSetBreakKey(`${prefixName}.design.iconColor`)
                                    }}
                                />
                            )
                        }}
                    />
                </ListItem4ByecodeUi>
            )}
            {isVisibleSetting && enablePlaceholderColor && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="占位符颜色" name={`${prefixName}.design.placeholderColor`} />
                    <Controller
                        control={control}
                        name={`${prefixName}.design.placeholderColor`}
                        render={({ field }) => {
                            return (
                                <FillColor
                                    label="占位符颜色"
                                    value={field.value}
                                    enabledBackgroundTypes={[BACKGROUND_TYPE.color]}
                                    onChange={val => {
                                        field.onChange(val)
                                        handleSetBreakKey(`${prefixName}.design.placeholderColor`)
                                    }}
                                />
                            )
                        }}
                    />
                </ListItem4ByecodeUi>
            )}
            {isVisibleSetting && enableLineColor && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label={lineLabel} name={`${prefixName}.design.lineColor`} />
                    <Controller
                        control={control}
                        name={`${prefixName}.design.lineColor`}
                        render={({ field }) => {
                            return (
                                <FillColor
                                    label={lineLabel}
                                    value={field.value}
                                    onChange={val => {
                                        field.onChange(val)
                                        handleSetBreakKey(`${prefixName}.design.lineColor`)
                                    }}
                                />
                            )
                        }}
                    />
                </ListItem4ByecodeUi>
            )}

            {isVisibleSetting && enableRadius && (
                <Controller
                    control={control}
                    name={`${prefixName}.design.radius`}
                    render={({ field }) => (
                        <RadiusSize
                            title={<InheritLabel label="圆角" name={`${prefixName}.design.radius`} />}
                            value={field.value}
                            onChange={val => {
                                field.onChange(val)
                                handleSetBreakKey(`${prefixName}.design.radius`)
                            }}
                        />
                    )}
                />
            )}

            {isVisibleSetting && enableBackground && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label={backgroundLabel} name={`${prefixName}.design.background`} />
                    <Controller
                        control={control}
                        name={`${prefixName}.design.background`}
                        render={({ field }) => {
                            return (
                                <BackgroundField
                                    customViewDsId={customViewDsId}
                                    enabledBackgroundTypes={colorSetting?.background}
                                    value={field.value}
                                    onChange={val => {
                                        field.onChange(val)
                                        handleSetBreakKey(`${prefixName}.design.background`)
                                    }}
                                />
                            )
                        }}
                    />
                </ListItem4ByecodeUi>
            )}

            {enableTextBorder && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="flex-start">
                    <InheritLabel label="描边" name="breakPoint.design.textBorder" />
                    <Flex direction="column" alignItems="flex-end" gap={8}>
                        <Controller
                            control={control}
                            name="breakPoint.design.textBorder"
                            render={({ field }) => (
                                <TextBorder
                                    customViewDsId={customViewDsId}
                                    value={field.value}
                                    enabledBackgroundTypes={colorSetting?.textBorder}
                                    onChange={val => {
                                        field.onChange(val)
                                        handleSetBreakKey('breakPoint.design.textBorder')
                                    }}
                                />
                            )}
                        />
                    </Flex>
                </ListItem4ByecodeUi>
            )}

            {isVisibleSetting && enableVeins && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="纹理" name={`${prefixName}.design.veins`} />
                    <Controller
                        control={control}
                        name={`${prefixName}.design.veins`}
                        render={({ field }) => (
                            <VeinsField
                                customViewDsId={customViewDsId}
                                value={field.value}
                                onChange={val => {
                                    field.onChange(val)
                                    handleSetBreakKey(`${prefixName}.design.veins`)
                                }}
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
            )}

            {isVisibleSetting && enableBorder && (
                <Controller
                    control={control}
                    name={`${prefixName}.design.border`}
                    render={({ field }) => (
                        <BorderField
                            customViewDsId={customViewDsId}
                            label={<InheritLabel label="描边" name={`${prefixName}.design.border`} />}
                            value={field.value}
                            onChange={v => {
                                if (isDeepEqual(v, field.value)) {
                                    return
                                }
                                field.onChange(v)
                                handleSetBreakKey(`${prefixName}.design.border`)
                            }}
                        />
                    )}
                />
            )}
            {isVisibleSetting && enableShadow && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="阴影" name={`${prefixName}.design.shadow`} />
                    <Controller
                        control={control}
                        name={`${prefixName}.design.shadow`}
                        render={({ field }) => (
                            <ShadowField
                                customViewDsId={customViewDsId}
                                value={field.value}
                                onChange={val => {
                                    field.onChange(val)
                                    handleSetBreakKey(`${prefixName}.design.shadow`)
                                }}
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
            )}

            {isVisibleSetting && enableBlur && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="背景模糊" name={`${prefixName}.design.blur`} />
                    <Controller
                        control={control}
                        name={`${prefixName}.design.blur`}
                        render={({ field }) => (
                            <Slider
                                style={{ width: 180 }}
                                min={0}
                                max={100}
                                step={1}
                                value={field.value}
                                onChange={v => {
                                    if (isDeepEqual(v, field.value)) {
                                        return
                                    }
                                    field.onChange(v)
                                    handleSetBreakKey(`${prefixName}.design.blur`)
                                }}
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
            )}

            {isVisibleSetting && enableCheckedColor && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="选中后" name={`${prefixName}.design.checkedColor`} />
                    <Controller
                        control={control}
                        name={`${prefixName}.design.checkedColor`}
                        render={({ field }) => (
                            <FillColor
                                label="选中后"
                                value={field.value}
                                enabledBackgroundTypes={[BACKGROUND_TYPE.color]}
                                onChange={val => {
                                    field.onChange(val)
                                    handleSetBreakKey(`${prefixName}.design.checkedColor`)
                                }}
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
            )}

            {isVisibleSetting && afterSlot}
        </>
    )
}
